import { useQuery } from "@tanstack/react-query";
import { useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { parseAbiItem, type Address } from "viem";
import { keelVaultFactoryAbi } from "../abi";
import type { RedKeel } from "../config/networks";
import { clienteLogs, logsHistoricos } from "../lib/cliente";

const PERF_FEE = parseAbiItem("event PerfFeeTaken(address indexed treasury, uint256 fee0, uint256 fee1)");
const FEES_WITHDRAWN = parseAbiItem("event FeesWithdrawn(uint256 profit0, uint256 profit1)");
const REINVERTIDAS = parseAbiItem("event ProfitReinvested(uint256 profit0, uint256 profit1)");

export type Ganancias = {
  /** TODO lo que la posicion ha generado, neto de la comision, por todos los caminos */
  ganado0: bigint;
  ganado1: bigint;
  /** lo que se ha mandado a la wallet */
  retirado0: bigint;
  retirado1: bigint;
  /** lo que se ha devuelto a la posicion */
  reinvertido0: bigint;
  reinvertido1: bigint;
  /** lo que se llevo Keellabs, para poder cruzarlo */
  comision0: bigint;
  comision1: bigint;
  cobros: number;
  /** los bps con los que se ha calculado; si algun dia cambian, el historico se desvia */
  bps: number;
};

/**
 * Cuanto ha ganado este vault DESDE QUE SE ABRIO.
 *
 * NO se puede leer del estado: `profit0/1` es solo lo que hay apartado AHORA y se vacia cada vez
 * que el dueño retira o reinvierte, asi que un vault que lleva meses cobrando enseñaria cero.
 *
 * Y NO vale sumar `FeesCollected`: ese evento solo lo emite `collectFees`, el cobro a demanda. Lo
 * que se cobra en cada REAJUSTE (`_closePosition`) y al quitar liquidez solo emite `ProfitHeld`,
 * que lleva el ACUMULADO y no el incremento — y con la reinversion marcada no emite nada. Contando
 * asi se pierde casi todo.
 *
 * El unico evento presente en los TRES caminos es `PerfFeeTaken`: `_takePerfFee` se llama en el
 * cobro a demanda, en el cierre de posicion y en `removeLiquidity`. Y la relacion es exacta:
 *
 *     comision = bruto * bps / 10000     ->     neto = comision * (10000 - bps) / bps
 *
 * Con los 1000 bps de hoy, neto = comision * 9. Tiene ademas la ventaja de que el usuario puede
 * cruzarlo: es literalmente lo que se llevo el treasury.
 */
export function useGanancias(vault: Address | null, red: RedKeel | undefined) {
  const config = useConfig();

  return useQuery({
    queryKey: ["ganancias", red?.clave, vault],
    enabled: !!vault && !!red,
    staleTime: 60_000,
    queryFn: async (): Promise<Ganancias> => {
      const client = getPublicClient(config, { chainId: red!.idCadena })!;
      const eventos = clienteLogs(red!);

      // De uno en uno: el RPC de Robinhood rechaza varios eventos a la vez (ver la nota de
      // Historial.tsx). Y por el cliente de EVENTOS, con troceo si hace falta: el RPC de lectura de
      // varias redes no sirve `eth_getLogs`, y donde no lo sirve esta pantalla decia «ganado 0»
      // como si la piscina no hubiera cobrado nunca.
      const pedir = async (evento: typeof PERF_FEE | typeof FEES_WITHDRAWN | typeof REINVERTIDAS) =>
        (await logsHistoricos<{ args: Record<string, unknown> }>(
          eventos, { address: vault!, event: evento }, red!
        )) ?? [];

      const [comisiones, retiros, reinversiones, bps] = await Promise.all([
        pedir(PERF_FEE), pedir(FEES_WITHDRAWN), pedir(REINVERTIDAS),
        client.readContract({ address: red!.factory, abi: keelVaultFactoryAbi, functionName: "perfFeeBps" })
          .catch(() => 1000),
      ]);

      const sumar = (logs: { args: Record<string, unknown> }[], a: string, b: string) =>
        logs.reduce<[bigint, bigint]>(
          (s, l) => [s[0] + ((l.args[a] as bigint) ?? 0n), s[1] + ((l.args[b] as bigint) ?? 0n)],
          [0n, 0n]
        );

      const [c0, c1] = sumar(comisiones as never, "fee0", "fee1");
      const [r0, r1] = sumar(retiros as never, "profit0", "profit1");
      const [v0, v1] = sumar(reinversiones as never, "profit0", "profit1");

      const b = BigInt(Number(bps) || 1000);
      const neto = (comision: bigint) => (b > 0n ? (comision * (10000n - b)) / b : 0n);

      return {
        ganado0: neto(c0), ganado1: neto(c1),
        retirado0: r0, retirado1: r1,
        reinvertido0: v0, reinvertido1: v1,
        comision0: c0, comision1: c1,
        cobros: comisiones.length,
        bps: Number(bps) || 1000,
      };
    },
  });
}
